import { AspectRatio, Box, Card, Grid, Group, Stack, Text } from "@mantine/core";
import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import HeatMap from "@components/common/HeatMap";
import {
  ContinuousPerformanceStageEvent,
  PercentagePoint,
  PercentageWithTimeStamp,
} from "types/tests";
import {
  StageBucket,
  buildStageBuckets,
  renderStageDisplayName,
  stagePalette,
  stagePaletteShade,
} from "../utils/cptStageMetrics";
import CPTSectionHeader from "./CPTSectionHeader";

interface CPTPerStageGazeSectionProps {
  stageEvents?: ContinuousPerformanceStageEvent[];
  gazePercentageData?: PercentageWithTimeStamp[] | null;
  sectionNumber?: number;
}

interface StageHeatmapTile {
  stage: StageBucket;
  samples: PercentagePoint[];
}

function partitionGazeByStage(
  buckets: StageBucket[],
  gaze: PercentageWithTimeStamp[] | null | undefined,
): StageHeatmapTile[] {
  if (!buckets.length) return [];
  return buckets.map((stage) => {
    const samples: PercentagePoint[] = [];
    if (gaze?.length && !stage.empty) {
      for (const p of gaze) {
        const t = p.timeStamp;
        if (t === undefined || t === null || !Number.isFinite(t)) continue;
        if (t >= stage.startTime && t < stage.endTime) samples.push(p);
      }
    }
    return { stage, samples };
  });
}

const StageHeatmapCard: React.FC<{ tile: StageHeatmapTile; t: TFunction }> = ({ tile, t }) => {
  const { stage, samples } = tile;
  const palette = stagePalette(stage);
  const shade = stagePaletteShade(palette);

  return (
    <Card
      withBorder
      radius="md"
      p="md"
      bg={`${palette.hue}.0`}
      style={{ borderColor: `var(--mantine-color-${palette.hue}-${shade})` }}
    >
      <Stack gap="xs">
        <Group justify="space-between" wrap="nowrap" align="flex-start">
          <Stack gap={0}>
            <Text size="sm" fw={700} c={`${palette.hue}.9`}>
              {renderStageDisplayName(stage, t)}
            </Text>
            <Text size="xs" c={`${palette.hue}.7`}>
              {stage.shortLabel} · {stage.rawName}
            </Text>
          </Stack>
          <Text size="xs" c="dimmed" fw={600}>
            {t("extraTests.continuousPerformance.result.perStageGaze.samples", {
              count: samples.length,
            })}
          </Text>
        </Group>
        <AspectRatio ratio={16 / 9}>
          <Box
            bd="1px solid gray.4"
            bg="gray.0"
            pos="relative"
            style={{ borderRadius: 4, overflow: "hidden" }}
          >
            {samples.length ? (
              <HeatMap percentages={samples} isRealtime={false} />
            ) : (
              <Box
                pos="absolute"
                display="grid"
                p={8}
                ta="center"
                style={{ inset: 0, placeItems: "center" }}
              >
                <Text size="xs" c="dimmed">
                  {t("extraTests.continuousPerformance.result.perStageGaze.noSamplesInStage")}
                </Text>
              </Box>
            )}
          </Box>
        </AspectRatio>
      </Stack>
    </Card>
  );
};

const CPTPerStageGazeSection: React.FC<CPTPerStageGazeSectionProps> = ({
  stageEvents,
  gazePercentageData,
  sectionNumber,
}) => {
  const { t } = useTranslation();
  const buckets = useMemo(() => buildStageBuckets(stageEvents), [stageEvents]);
  const tiles = useMemo(
    () => partitionGazeByStage(buckets, gazePercentageData ?? null),
    [buckets, gazePercentageData],
  );

  if (!tiles.length) return null;

  const hasAnySamples = tiles.some((tile) => tile.samples.length > 0);
  const ns = "extraTests.continuousPerformance.result.perStageGaze";

  return (
    <Stack gap="xl">
      <CPTSectionHeader number={sectionNumber} title={t(`${ns}.title`)} />
      <Text size="sm" c="dimmed">
        {t(`${ns}.description`)}
      </Text>
      {hasAnySamples ? (
        <Grid gutter="md">
          {tiles.map((tile) => (
            <Grid.Col
              key={tile.stage.key}
              span={{ base: 12, sm: 6, md: 4, lg: 3 }}
              className="cpt-col-3"
            >
              <StageHeatmapCard tile={tile} t={t} />
            </Grid.Col>
          ))}
        </Grid>
      ) : (
        <Card withBorder radius="md" p="xl">
          <Text c="dimmed">{t(`${ns}.noSession`)}</Text>
        </Card>
      )}
    </Stack>
  );
};

export default CPTPerStageGazeSection;
